import React, { useState } from 'react';
import { UserAccount } from '../types';
import { Image } from './Image';

interface ProfileSettingsProps {
    user: UserAccount;
    onClose: () => void;
    onSave: (updates: { 
        email?: string; 
        full_name?: string;
        workshop_name?: string;
        workshop_phone?: string;
        workshop_address?: string;
        workshop_slogan?: string;
        workshop_logo_url?: string;
    }) => Promise<void>;
    onUploadLogo?: (file: File) => Promise<string>;
    isDark?: boolean;
    onToggleTheme?: () => void;
    isEmbedded?: boolean;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({
    user,
    onClose,
    onSave,
    onUploadLogo,
    isDark = false,
    onToggleTheme,
    isEmbedded = false,
}) => {
    const [email, setEmail] = useState(user.email || '');
    const [fullName, setFullName] = useState(user.full_name || '');
    
    // Workshop info
    const [wName, setWName] = useState(user.workshop?.name || '');
    const [wPhone, setWPhone] = useState(user.workshop?.phone || '');
    const [wAddress, setWAddress] = useState(user.workshop?.address || '');
    const [wSlogan, setWSlogan] = useState(user.workshop?.slogan || '');
    const [wLogoUrl, setWLogoUrl] = useState(user.workshop?.logo_url || '');

    const [loading, setLoading] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !onUploadLogo) return;

        setUploadingLogo(true);
        setError(null);
        try {
            const url = await onUploadLogo(file);
            setWLogoUrl(url);
        } catch (err: any) {
            setError(err?.message || "Échec de l'upload du logo");
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            await onSave({
                email: email.trim() || undefined,
                full_name: fullName.trim() || undefined,
                workshop_name: wName.trim() || undefined,
                workshop_phone: wPhone.trim() || undefined,
                workshop_address: wAddress.trim() || undefined,
                workshop_slogan: wSlogan.trim() || undefined,
                workshop_logo_url: wLogoUrl.trim() || undefined,
            });
            setSuccess(true);
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (err: any) {
            setError(err?.message || 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    // Styles dynamiques selon le thème courant
    // Styles dynamiques utilisant les tokens du design system (index.css)
    const card = isEmbedded
        ? 'w-full max-w-lg mx-auto'
        : 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4';
    
    const modal = isEmbedded
        ? 'w-full flex flex-col' 
        : 'relative w-full max-w-lg rounded-3xl bg-[var(--bg-card)] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-[var(--border-color)]';

    const headerCls = 'flex items-center justify-between border-b border-[var(--border-color)] px-6 py-4 sticky top-0 bg-[var(--bg-card)] z-10';
    const titleCls = 'text-xl font-bold t-text-primary';
    const closeCls = 'rounded-full p-2 t-text-muted hover:bg-[var(--bg-subtle)] transition';
    const sectionTitle = 'text-xs font-bold t-text-muted uppercase tracking-widest';
    const inputCls = 't-input w-full';
    const labelCls = 'block text-sm font-medium t-text-secondary mb-1.5';
    const infoBoxCls = 'rounded-2xl p-4 border border-[var(--border-color)] bg-[var(--bg-subtle)]';
    const infoLabelCls = 'text-xs t-text-muted mb-0.5';
    const infoValueCls = 'font-semibold t-text-primary';
    const dividerCls = 'space-y-4 pt-6 border-t border-[var(--border-color)]';
    const cancelBtn = 'flex-1 rounded-xl border border-[var(--border-color)] px-4 py-3 text-sm font-medium t-text-secondary hover:bg-[var(--bg-subtle)] transition';

    return (
        <div className={card}>
            <div className={modal}>
                {/* Header */}
                <div className={headerCls}>
                    <h2 className={titleCls}>⚙️ Paramètres du compte</h2>
                    {!isEmbedded && (
                        <button onClick={onClose} className={closeCls}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Content - Scrollable container or normal flow if embedded */}
                <form onSubmit={handleSubmit} className={`${isEmbedded ? '' : 'overflow-y-auto'} p-6 space-y-5`}>
                    {/* Section 0: Thème */}
                    {onToggleTheme && (
                        <div className={dividerCls.replace('pt-6 border-t border-[var(--border-color)]', '')}>
                            <h3 className={sectionTitle}>Apparence</h3>
                            <button
                                type="button"
                                onClick={onToggleTheme}
                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition ${
                                    isDark
                                        ? 'bg-indigo-500/10 border-indigo-500/30 hover:bg-indigo-500/20'
                                        : 'bg-indigo-50 border-indigo-100 hover:bg-indigo-100'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-xl">{isDark ? '🌙' : '☀️'}</span>
                                    <div className="text-left">
                                        <p className="text-sm font-semibold t-text-primary">
                                            {isDark ? 'Thème Nuit Émeraude' : 'Thème Clair'}
                                        </p>
                                        <p className="text-xs t-text-secondary">
                                            {isDark ? 'Passer au mode clair' : 'Passer au mode sombre'}
                                        </p>
                                    </div>
                                </div>
                                {/* Toggle pill */}
                                <div className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                                    isDark ? 'bg-indigo-500' : 'bg-gray-200'
                                }`}>
                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${
                                        isDark ? 'translate-x-6' : 'translate-x-0'
                                    }`} />
                                </div>
                            </button>
                        </div>
                    )}
                    {/* Section 1: Compte */}
                    <div className={`space-y-4 ${onToggleTheme ? dividerCls : ''}`}>
                        <h3 className={sectionTitle}>Compte</h3>
                        
                        {/* Info utilisateur */}
                        <div className={infoBoxCls}>
                            <p className={infoLabelCls}>Téléphone</p>
                            <p className={infoValueCls}>{user.phone || 'Non renseigné'}</p>
                        </div>

                    {/* Nom complet */}
                    <div>
                        <label htmlFor="profile-name" className={labelCls}>
                            Nom complet
                        </label>
                        <input
                            id="profile-name"
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Votre nom"
                            className={inputCls}
                        />
                    </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="profile-email" className={labelCls}>
                                Adresse e-mail
                            </label>
                            <input
                                id="profile-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="atelier@example.com"
                                className={inputCls}
                            />
                            {!user.email && (
                                <p className="mt-2 text-[10px] p-2 rounded-lg bg-amber-500/10 text-amber-500 border border-amber-500/20">
                                    💡 Ajoutez un email pour pouvoir réinitialiser votre mot de passe en cas d'oubli.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Section: Stockage & Abonnement */}
                    <div className={dividerCls}>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className={sectionTitle}>Stockage & Plan</h3>
                            {user.subscription.plan && (
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                                    isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
                                }`}>
                                    {user.subscription.plan}
                                </span>
                            )}
                        </div>

                        {user.subscription.storage_quota_mb !== undefined && (
                            <div className="space-y-3">
                                <div className="flex justify-between items-end text-xs mb-1">
                                    <span className={infoLabelCls}>
                                        {user.subscription.storage_used_mb ?? 0} Mo sur {user.subscription.storage_quota_mb === -1 ? 'Illimité' : `${user.subscription.storage_quota_mb} Mo`}
                                    </span>
                                    {user.subscription.storage_quota_mb && user.subscription.storage_quota_mb > 0 && (
                                        <span className={isDark ? 'text-slate-400' : 'text-gray-500 font-medium'}>
                                            {Math.round(((user.subscription.storage_used_mb ?? 0) / user.subscription.storage_quota_mb) * 100)}%
                                        </span>
                                    )}
                                </div>
                                {user.subscription.storage_quota_mb && user.subscription.storage_quota_mb > 0 ? (
                                    <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                        <div 
                                            className={`h-full transition-all duration-700 ease-out ${
                                                ((user.subscription.storage_used_mb ?? 0) / user.subscription.storage_quota_mb) > 0.9 
                                                    ? 'bg-red-500' 
                                                    : ((user.subscription.storage_used_mb ?? 0) / user.subscription.storage_quota_mb) > 0.7
                                                    ? 'bg-amber-500'
                                                    : 'bg-indigo-500'
                                            }`}
                                            style={{ width: `${Math.min(100, ((user.subscription.storage_used_mb ?? 0) / user.subscription.storage_quota_mb) * 100)}%` }}
                                        />
                                    </div>
                                ) : (
                                    <div className={`w-full h-2 rounded-full ${isDark ? 'bg-indigo-500/20' : 'bg-indigo-50'} flex items-center px-1`}>
                                        <div className="h-1 w-full bg-indigo-500 rounded-full animate-pulse opacity-20" />
                                    </div>
                                )}
                                
                                {user.subscription.storage_quota_mb && user.subscription.storage_quota_mb > 0 && ((user.subscription.storage_used_mb ?? 0) / user.subscription.storage_quota_mb) >= 0.9 && (
                                    <p className="text-[10px] text-red-500 font-medium flex items-center gap-1 animate-pulse">
                                        <span>⚠️</span> Espace de sauvegarde presque plein.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Section 2: Mon Atelier (Premium) */}
                    <div className={dividerCls}>
                        <div className="flex items-center justify-between">
                            <h3 className={sectionTitle}>Mon Atelier</h3>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                                isDark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'
                            }`}>Infos Reçu</span>
                        </div>

                        {/* Nom Atelier */}
                        <div>
                            <label htmlFor="w-name" className={labelCls}>
                                Nom de l'Atelier / Entreprise
                            </label>
                            <input
                                id="w-name"
                                type="text"
                                value={wName}
                                onChange={(e) => setWName(e.target.value)}
                                placeholder="Ex: Élégance Couture"
                                className={inputCls}
                            />
                        </div>

                        {/* Téléphone Atelier */}
                        <div>
                            <label htmlFor="w-phone" className={labelCls}>
                                Téléphone (Atelier)
                            </label>
                            <input
                                id="w-phone"
                                type="text"
                                value={wPhone}
                                onChange={(e) => setWPhone(e.target.value)}
                                placeholder="Ex: +229 00 00 00 00"
                                className={inputCls}
                            />
                        </div>

                        {/* Adresse Atelier */}
                        <div>
                            <label htmlFor="w-addr" className={labelCls}>
                                Adresse / Localisation
                            </label>
                            <textarea
                                id="w-addr"
                                value={wAddress}
                                onChange={(e) => setWAddress(e.target.value)}
                                placeholder="Ex: Cotonou, rue 123..."
                                rows={2}
                                className={inputCls + ' resize-none'}
                            />
                        </div>

                        {/* Slogan */}
                        <div>
                            <label htmlFor="w-slogan" className={labelCls}>
                                Slogan ou Message (Optionnel)
                            </label>
                            <input
                                id="w-slogan"
                                type="text"
                                value={wSlogan}
                                onChange={(e) => setWSlogan(e.target.value)}
                                placeholder="Ex: L'art de la haute couture"
                                className={inputCls}
                            />
                        </div>

                        {/* Logo Upload */}
                        <div className="space-y-2">
                            <label className={labelCls}>
                                Logo de l'Atelier
                            </label>
                            
                            <div className={`flex items-center gap-4 p-4 rounded-xl border ${
                                isDark ? 'border-white/10 bg-white/5' : 'border-gray-100 bg-gray-50/50'
                            }`}>
                                <div className={`relative h-20 w-20 flex-none rounded-lg border overflow-hidden shadow-sm flex items-center justify-center ${
                                    isDark ? 'border-white/10 bg-slate-900' : 'border-gray-200 bg-white'
                                }`}>
                                    {wLogoUrl ? (
                                        <Image 
                                            src={wLogoUrl} 
                                            alt="Logo workshop" 
                                            className="h-full w-full object-contain"
                                        />
                                    ) : (
                                        <div className="text-2xl text-gray-300">🏢</div>
                                    )}
                                    {uploadingLogo && (
                                        <div className={`absolute inset-0 flex items-center justify-center ${
                                            isDark ? 'bg-slate-900/80' : 'bg-white/80'
                                        }`}>
                                            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex-1 space-y-1">
                                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                        Importez votre logo pour un reçu premium.
                                    </p>
                                    <label className="inline-block">
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            disabled={uploadingLogo}
                                        />
                                        <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition ${
                                            uploadingLogo 
                                                ? (isDark ? 'bg-white/5 text-slate-500' : 'bg-gray-100 text-gray-400') 
                                                : (isDark ? 'bg-white/10 text-indigo-400 border border-white/10 hover:bg-white/20' : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50 shadow-sm')
                                        }`}>
                                            {wLogoUrl ? 'Modifier le logo' : 'Ajouter un logo'}
                                        </span>
                                    </label>
                                    {wLogoUrl && (
                                        <button
                                            type="button"
                                            onClick={() => setWLogoUrl('')}
                                            className="ml-3 text-xs text-red-500 hover:text-red-400"
                                        >
                                            Supprimer
                                        </button>
                                    )}
                                </div>
                            </div>
                            <input 
                                type="hidden" 
                                value={wLogoUrl} 
                            />
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 border border-red-200">
                            {error}
                        </div>
                    )}

                    {/* Success */}
                    {success && (
                        <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 border border-green-200">
                            ✅ Paramètres mis à jour avec succès !
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-3 pt-2">
                        {!isEmbedded && (
                            <button
                                type="button"
                                onClick={onClose}
                                className={cancelBtn}
                            >
                                Annuler
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className={`${isEmbedded ? 'w-full' : 'flex-1'} rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition`}
                        >
                            {loading ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
